import { useState } from "react";

import { ApiError } from "@/api/client";
import { useCreateActivity, useTimeline, useUpdateActivity } from "@/api/crm";
import type { ActivityKind, TimelineItem } from "@/api/types";
import { Alert, Spinner } from "@/components/ui";
import { Icon, type IconName } from "@/components/icons";
import { formatDate } from "@/components/ui";
import { fromLocalInput, toLocalInput } from "@/lib/format";
import { zoneLabel } from "@/lib/time";

type Target = { type: "contact" | "account" | "deal"; id: string };

const KINDS: { kind: ActivityKind; label: string }[] = [
  { kind: "note", label: "Note" }, { kind: "call", label: "Call" }, { kind: "meeting", label: "Meeting" },
  { kind: "visit", label: "Site visit" }, { kind: "email", label: "Email" }, { kind: "whatsapp", label: "WhatsApp" },
  { kind: "task", label: "Task" },
];

const ACTION_LABEL: Record<string, string> = { create: "Created", update: "Updated", delete: "Deleted" };

function show(v: unknown): string {
  if (v === null || v === undefined || v === "") return "empty";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function humanKey(key: string, labels: Record<string, string>): string {
  return labels[key] ?? key.replace(/^custom\./, "").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** Log a note, call, meeting, visit, email or WhatsApp, or schedule a task, against a record. */
export function ActivityComposer({ target }: { target: Target }) {
  const create = useCreateActivity();
  const [kind, setKind] = useState<ActivityKind>("note");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [due, setDue] = useState(() => toLocalInput(new Date(Date.now() + 86_400_000)));
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        kind, subject: subject.trim() || KINDS.find((k) => k.kind === kind)!.label, body: body.trim() || null,
        due_at: kind === "task" ? fromLocalInput(due) : null, [`${target.type}_id`]: target.id,
      });
      setSubject(""); setBody("");
    } catch (err) {
      setError(err instanceof ApiError ? Object.values(err.fieldErrors())[0] ?? err.message : "Could not save");
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3" aria-label="Log activity">
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Activity type">
        {KINDS.map((k) => (
          <button key={k.kind} type="button" role="radio" aria-checked={kind === k.kind} onClick={() => setKind(k.kind)}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${kind === k.kind ? "border-brand bg-brand-soft text-brand" : "border-line text-muted hover:bg-canvas"}`}>
            <Icon name={k.kind as IconName} className="h-3.5 w-3.5" />{k.label}
          </button>
        ))}
      </div>
      {error && <Alert>{error}</Alert>}
      <input className="input" aria-label="Subject" placeholder={kind === "task" ? "What needs to happen?" : "Summary"} value={subject} onChange={(e) => setSubject(e.target.value)} />
      <textarea className="input min-h-16" aria-label="Details" placeholder="Details (optional)" value={body} onChange={(e) => setBody(e.target.value)} />
      <div className="flex flex-wrap items-end justify-between gap-2">
        {kind === "task" ? (
          <label className="text-xs text-muted">Due ({zoneLabel()})
            <input type="datetime-local" className="input mt-1 w-auto" aria-label="Due" value={due} onChange={(e) => setDue(e.target.value)} />
          </label>
        ) : <span />}
        <button type="submit" className="btn-primary" disabled={create.isPending}>{kind === "task" ? "Add task" : "Log activity"}</button>
      </div>
    </form>
  );
}

function TaskCheckbox({ id, subject, completed }: { id: string; subject: string; completed: boolean }) {
  const update = useUpdateActivity();
  const [done, setDone] = useState(completed);
  return (
    <label className="flex shrink-0 items-center gap-1 text-xs text-muted">
      <input type="checkbox" className="h-4 w-4 accent-brand" checked={done} aria-label={`Mark “${subject}” done`}
        onChange={(e) => { const next = e.target.checked; setDone(next); update.mutate({ id, completed: next }, { onError: () => setDone(!next) }); }} />
      Done
    </label>
  );
}

function Entry({ item, labels }: { item: TimelineItem; labels: Record<string, string> }) {
  if (item.type === "activity" && item.activity) {
    const a = item.activity;
    const isTask = a.kind === "task";
    return (
      <li className="relative pb-5 pl-9">
        <span className={`absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full ${isTask && !a.completed_at ? "bg-warn/10 text-warn" : "bg-brand-soft text-brand"}`}>
          <Icon name={a.kind as IconName} className="h-3.5 w-3.5" />
        </span>
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium ${isTask && a.completed_at ? "text-muted line-through" : ""}`}>{a.subject}</p>
          {isTask && <TaskCheckbox key={`${a.id}-${!!a.completed_at}`} id={a.id} subject={a.subject} completed={!!a.completed_at} />}
        </div>
        {a.body && <p className="mt-0.5 whitespace-pre-line text-sm text-ink/80">{a.body}</p>}
        <p className="mt-0.5 text-xs text-muted">
          {isTask ? `Due ${formatDate(a.due_at!)}` : formatDate(a.occurred_at)}{a.created_by && ` · ${a.created_by.full_name}`}
          {a.is_overdue && <span className="ml-1 font-medium text-danger">Overdue</span>}
        </p>
      </li>
    );
  }
  const c = item.change!;
  const entries = Object.entries(c.changes);
  return (
    <li className="relative pb-5 pl-9">
      <span className="absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full bg-canvas text-muted"><Icon name="change" className="h-3.5 w-3.5" /></span>
      <p className="text-sm font-medium">{ACTION_LABEL[c.action] ?? c.action}{c.actor && <span className="font-normal text-muted"> by {c.actor}</span>}</p>
      <p className="text-xs text-muted">{formatDate(item.at)}</p>
      {c.action === "update" && (
        <ul className="mt-1 space-y-0.5 text-xs text-muted">
          {entries.map(([k, v]) => <li key={k} className="break-words"><span className="font-medium text-ink">{humanKey(k, labels)}</span>: {show(v.from)} → {show(v.to)}</li>)}
        </ul>
      )}
    </li>
  );
}

export function Timeline({ target, labels = {} }: { target: Target; labels?: Record<string, string> }) {
  const timeline = useTimeline(target.type, target.id);
  if (timeline.isLoading) return <Spinner />;
  if (timeline.error) return <Alert>{timeline.error.message}</Alert>;
  const items = timeline.data ?? [];
  if (!items.length) return <p className="text-sm text-muted">No activity yet.</p>;
  return <ol className="border-l border-line pl-0 [&>li]:-ml-[14px]" aria-label="Timeline">{items.map((i, n) => <Entry key={n} item={i} labels={labels} />)}</ol>;
}
